import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useAuthentication } from "common/auth/hooks";
import { FetchError } from "common/fetch/errors";
import { StatusCode } from "common/fetch/types";
import {
  IAddress,
  IClientUpdateFields,
  IPatient,
  IPatientWrite,
  PatientBreed,
  IPatientWriteBody,
  PatientColor,
  IClientProfileUpdate,
  ClientUserWithContacts,
} from "common/types";
import {
  AddressConfirmMutation,
  AddressWriteMutation,
  IAddressFormValues,
  IVerifyAddress,
} from "common/types/addresses";
import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import { Optional } from "common/utils/types";
import { toPhone } from "common/utils/formatting";

import { AuthUsernameType } from "client/analytics/types";
import {
  useSendPostHogLoginAttempted,
  useSendPostHogLoginFailed,
  useSendPostHogLoginSucceeded,
  useSendPostHogPasswordResetRequested,
  useSendPostHogPasswordResetRequestSucceeded,
  useSendPostHogPasswordResetRequestFailed,
  useSendPostHogSignupCompleted,
  useSendPostHogSignupFailed,
  useSendPostHogSignupCodeRequested,
  useSendPostHogSignupCodeRequestSucceeded,
  useSendPostHogSignupCodeRequestFailed,
  useGetPostHogDistinctId,
} from "client/analytics/utils";
import {
  LoginErrorData,
  PasswordResetRequestErrorData,
  SignupErrorData,
  SignupVerificationCodeErrorData,
  SignupVerificationCodeErrorKind,
  IClient,
  IProfile,
  IPasswordLogin,
  PasswordChangeFormValues,
  ResetPasswordConfirm,
  ResetPasswordRequestBody,
  ResetPasswordRequest,
  IProfileLookupResult,
  IProfileLookup,
  VerifyCodeSignup,
  IVerifySmsCodeSignupResult,
  ILoginPhoneValidationResults,
  LoginPhoneValidationSearch,
  ISendVerificationCode,
  PrimaryPhoneMutation,
  PrimaryPhone,
  PatientDetail,
  PatientUpdate,
  PatientUpdateBody,
} from "client/accounts/types";
import { urls as routeUrls } from "client/routes";
import { useInvalidateClientPrimaryContactPreferences } from "client/inbox/hooks/invalidate";

import { keys, urls } from "./constants";
import { keys as inboxKeys } from "../../inbox/hooks/constants";
import { useInvalidateClient } from "./invalidate";
import { getSignupErrorDisplay, patientWriteToPatientUpdateBody } from "../utils";

const noop = () => undefined;

export const setClientData = (queryClient: QueryClient, client: IClient) => {
  queryClient.setQueryData(keys.activeClientDetail(), client);
};

export const updateClientData = (
  queryClient: QueryClient,
  updates: Partial<IClient> | ((client: IClient) => IClient),
) => {
  const current = queryClient.getQueryData<IClient>(keys.activeClientDetail());
  if (!current) {
    return;
  }
  const updateFn =
    typeof updates === "function" ? updates : (client: IClient) => ({ ...client, ...updates });
  setClientData(queryClient, updateFn(current));
};

export const useClientQuery = () => {
  const { isAuthenticated } = useAuthentication();
  return useFetchQuery<IClient>(keys.activeClientDetail(), {
    fetch: { url: urls.client() },
    config: { enabled: isAuthenticated },
  });
};

export const useClientUserQuery = () => {
  const { isAuthenticated } = useAuthentication();
  return useFetchQuery<ClientUserWithContacts>(keys.activeClientUserDetail(), {
    fetch: { url: urls.clientUser() },
    config: { enabled: isAuthenticated },
  });
};

export const useUpdateClientMutation = ({ onSuccess = noop }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<IClientUpdateFields, IClient>({
    fetch: (body) => ({ method: "PATCH", url: urls.client(), body }),
    config: {
      onSuccess: (client) => {
        setClientData(queryClient, client);
        queryClient.invalidateQueries({
          queryKey: inboxKeys.primaryContactPreferencesDetail(),
        });
        onSuccess();
      },
    },
  });
};

export const useUpdatePasswordMutation = ({
  onSuccess = noop,
}: { onSuccess?: () => void } = {}) => {
  return useFetchMutation<PasswordChangeFormValues, IProfile>({
    fetch: (body) => ({ method: "PUT", url: urls.updatePassword(), body }),
    config: {
      onSuccess,
    },
  });
};

export const useRequestResetPasswordLink = ({
  onSuccess,
}: {
  onSuccess: (response: ResetPasswordConfirm) => void;
}) => {
  const sendRequested = useSendPostHogPasswordResetRequested();
  const sendRequestSucceeded = useSendPostHogPasswordResetRequestSucceeded();
  const sendRequestFailed = useSendPostHogPasswordResetRequestFailed();
  const getPostHogDistinctId = useGetPostHogDistinctId();
  return useFetchMutation<
    ResetPasswordRequest,
    ResetPasswordConfirm,
    void,
    ResetPasswordRequestBody,
    FetchError<ResetPasswordRequestBody, PasswordResetRequestErrorData>
  >({
    fetch: (body) => ({
      method: "POST",
      url: urls.resetPassword(),
      body: { ...body, posthog_distinct_id: getPostHogDistinctId() },
    }),
    config: {
      onMutate: (variables) => {
        sendRequested({ username: variables.username });
      },
      onSuccess: (response, variables) => {
        sendRequestSucceeded({ username: variables.username });
        onSuccess(response);
      },
      onError: (error, variables) => {
        sendRequestFailed({
          username: variables.username,
          error_category: error.data?.kind ?? null,
          error_message: error.errors?.username?.[0] ?? error.detail ?? null,
        });
      },
    },
  });
};

export const useCreateAddressMutation = ({
  onSuccess,
}: {
  onSuccess: (address: IAddress) => void;
}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<AddressWriteMutation, IAddress>({
    fetch: (body) => ({ method: "POST", url: urls.addresses(), body }),
    config: {
      onSuccess: (data, variables) => {
        updateClientData(queryClient, (client) => {
          const replacedAddress = client.addresses.find(
            (address) => address.id === variables.replaces_address_id,
          );
          return {
            ...client,
            addresses: [
              ...client.addresses.filter((address) => address.id !== variables.replaces_address_id),
              {
                ...data,
                paused_subscription_count: replacedAddress?.paused_subscription_count ?? 0,
                active_subscription_count: replacedAddress?.active_subscription_count ?? 0,
              },
            ],
          };
        });
        queryClient.invalidateQueries({ queryKey: keys.activeClientDetail() });
        onSuccess(data);
      },
    },
  });
};

export const useDeleteAddressMutation = (
  addressId: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<void, IAddress>({
    fetch: () => ({ method: "DELETE", url: urls.address(addressId) }),
    config: {
      onSuccess: () => {
        updateClientData(queryClient, (current) => ({
          ...current,
          addresses: current.addresses.filter((a) => a.id !== addressId),
        }));
        queryClient.invalidateQueries({ queryKey: keys.activeClientDetail() });
        onSuccess();
      },
    },
  });
};

export const useConfirmAddressMutation = (
  addressId: number,
  { onSuccess }: { onSuccess: () => void },
) => {
  return useFetchMutation<void, IAddress, IAddress, AddressConfirmMutation>({
    fetch: () => ({
      method: "PATCH",
      url: urls.address(addressId),
      body: { is_user_confirmed: true },
    }),
    config: {
      onSuccess,
    },
  });
};

export const useVerifyAddressMutation = ({
  onSuccess,
}: {
  onSuccess: (data: IVerifyAddress) => void;
}) => {
  return useFetchMutation<IAddressFormValues, IVerifyAddress>({
    fetch: (body) => ({ method: "POST", url: urls.addressVerify(), body }),
    config: {
      onSuccess,
    },
  });
};

export const useCreatePatientMutation = ({
  onSuccess = noop,
}: { onSuccess?: (patient: IPatient) => void } = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<IPatientWrite, IPatient, IPatient, IPatientWriteBody>({
    fetch: (body) => ({
      method: "POST",
      url: urls.patients(),
      body: {
        ...body,
        primary_breed: body.primary_breed?.value ?? "",
        secondary_breeds: body.secondary_breeds.map((b) => b.value),
        color: body.color?.value ?? "",
      },
    }),
    config: {
      onSuccess: (patient) => {
        updateClientData(queryClient, (current) => ({
          ...current,
          // image is always null on create
          patients: [...current.patients, patient].map((p) => ({
            ...p,
            image: null,
          })),
        }));
        queryClient.invalidateQueries({
          queryKey: keys.activeClientDetail(),
        });
        onSuccess(patient);
      },
    },
  });
};

export const useUpdatePatientMutation = (
  patientId: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<
    Partial<PatientUpdate>,
    PatientDetail,
    PatientDetail,
    Partial<PatientUpdateBody>
  >({
    fetch: (body) => ({
      url: urls.patient(patientId),
      body: patientWriteToPatientUpdateBody(body),
      method: "PATCH",
    }),
    config: {
      onSuccess: (data) => {
        updateClientData(queryClient, (current) => ({
          ...current,
          patients: current.patients.map((p) =>
            p.id === patientId ? { ...data, image: p.image } : p,
          ),
        }));
        queryClient.invalidateQueries({ queryKey: keys.activeClientDetail() });
        queryClient.invalidateQueries({ queryKey: keys.patients() });
        onSuccess();
      },
    },
  });
};

export const usePatientQuery = (id: Optional<number>) => {
  return useFetchQuery<IPatient>(keys.patientDetail(id ?? 0), {
    fetch: { url: urls.patient(id ?? 0) },
    config: { enabled: !!id },
  });
};

export const usePatientBreedsQuery = () => {
  return useFetchQuery<PatientBreed[]>(keys.patientBreedsList(), {
    fetch: { url: urls.patientBreeds() },
  });
};

export const usePatientColorsQuery = () => {
  return useFetchQuery<PatientColor[]>(keys.patientColorsList(), {
    fetch: { url: urls.patientColors() },
  });
};

export const useUpdateProfileMutation = ({ onSuccess = noop }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const invalidatePrimaryContactPreferences = useInvalidateClientPrimaryContactPreferences();
  return useFetchMutation<Partial<IClientProfileUpdate>, IProfile>({
    fetch: (body) => ({ method: "PATCH", url: urls.profile(), body }),

    config: {
      onSuccess: (_) => {
        queryClient.invalidateQueries({ queryKey: keys.activeClientDetail() });
        queryClient.invalidateQueries({
          queryKey: keys.activeClientUserDetail(),
        });
        // updating the profile can switch the primary contact number//email which will affect the primary contact
        // preferences
        invalidatePrimaryContactPreferences();
        onSuccess();
      },
    },
  });
};

/**
 * Logs the user in and hard redirects to the url specified by `next`.
 * The hard redirect is required to clear any global context or react-query
 * `initialData` tied to the user context (including the CSRF token)
 */
export const usePasswordLoginMutation = (next: string) => {
  // special loading state is tracked to ensure that consumers still display
  // the mutation as loading while the redirect is in progress
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const sendLoginAttempted = useSendPostHogLoginAttempted();
  const sendLoginSucceeded = useSendPostHogLoginSucceeded();
  const sendLoginFailed = useSendPostHogLoginFailed();
  const mutation = useFetchMutation<
    IPasswordLogin,
    IProfile,
    void,
    IPasswordLogin,
    FetchError<void, LoginErrorData>
  >({
    fetch: (body) => ({ method: "POST", url: urls.login(), body }),
    config: {
      onMutate: (variables) => {
        setIsLoggingIn(true);
        sendLoginAttempted({ username: variables.username });
      },
      onSuccess: (_data, variables) => {
        sendLoginSucceeded({ username: variables.username });
        window.location.replace(next);
      },
      onError: (error, variables) => {
        setIsLoggingIn(false);
        sendLoginFailed({
          username: variables.username,
          error_category: error.data?.kind ?? null,
          error_message: error.detail ?? null,
        });
      },
    },
  });
  return { ...mutation, isLoading: mutation.isPending || isLoggingIn };
};

export const useProfileLookupMutation = ({
  onSuccess = noop,
}: {
  onSuccess?: (result: IProfileLookupResult) => void;
} = {}) => {
  return useFetchMutation<IProfileLookup, IProfileLookupResult>({
    fetch: (body: IProfileLookup) => ({
      method: "POST",
      url: urls.profileLookup(),
      body,
    }),
    config: {
      onSuccess,
    },
  });
};

export const useSendVerificationCodeMutation = ({
  onSuccess = noop,
  onError = noop,
  signupTracking,
}: {
  onSuccess?: () => void;
  onError?: () => void;
  // When provided (signup flow only), emit PostHog code-send tracking events.
  // Omitted by the account-management drawers so they stay untracked.
  signupTracking?: { usernameType: AuthUsernameType; isResend: boolean };
} = {}) => {
  const sendRequested = useSendPostHogSignupCodeRequested();
  const sendRequestSucceeded = useSendPostHogSignupCodeRequestSucceeded();
  const sendRequestFailed = useSendPostHogSignupCodeRequestFailed();
  return useFetchMutation<
    ISendVerificationCode,
    void,
    void,
    ISendVerificationCode,
    FetchError<ISendVerificationCode, SignupVerificationCodeErrorData>
  >({
    fetch: (body) => ({
      method: "POST",
      url: urls.sendVerificationCode(),
      body,
    }),
    config: {
      onMutate: () => {
        if (signupTracking) {
          sendRequested({
            username_type: signupTracking.usernameType,
            is_resend: signupTracking.isResend,
          });
        }
      },
      onSuccess: () => {
        if (signupTracking) {
          sendRequestSucceeded({
            username_type: signupTracking.usernameType,
            is_resend: signupTracking.isResend,
          });
        }
        onSuccess();
      },
      onError: (error) => {
        if (signupTracking) {
          sendRequestFailed({
            username_type: signupTracking.usernameType,
            is_resend: signupTracking.isResend,
            error_category:
              error.status === StatusCode.TooManyRequests
                ? SignupVerificationCodeErrorKind.RateLimited
                : (error.data?.kind ?? null),
            error_message:
              error.errors?.number?.[0] ??
              error.errors?.email?.[0] ??
              error.errors?.profile?.first_name?.[0] ??
              error.errors?.profile?.last_name?.[0] ??
              error.errors?.profile?.new_password?.[0] ??
              error.errors?.non_field_errors?.[0] ??
              error.detail ??
              null,
          });
        }
        onError();
      },
    },
  });
};

export const usePrimaryPhoneMutation = ({ onSuccess = noop }: { onSuccess?: () => void }) => {
  const invalidateClient = useInvalidateClient();
  const invalidatePrimaryContactPreferences = useInvalidateClientPrimaryContactPreferences();
  const queryClient = useQueryClient();

  return useFetchMutation<PrimaryPhoneMutation, PrimaryPhone>({
    fetch: (body) => ({ method: "POST", url: urls.primaryPhone(), body }),
    config: {
      onSuccess: () => {
        invalidateClient();
        invalidatePrimaryContactPreferences();
        queryClient.invalidateQueries({
          queryKey: keys.activeClientUserDetail(),
        });
        onSuccess();
      },
    },
  });
};

/**
 * Logs the user in with an SMS code and hard redirects
 * to the url specified by `next`. The hard redirect is required
 * to clear any global context or react-query `initialData`
 * tied to the user context (including the CSRF token)
 */
export const useSignUpMutation = (
  usernameType: AuthUsernameType,
  next: string = routeUrls.home.root.to(),
) => {
  // special loading state is tracked to ensure that consumers still display
  // the mutation as loading while the redirect is in progress
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const sendSignupCompleted = useSendPostHogSignupCompleted();
  const sendSignupFailed = useSendPostHogSignupFailed();
  const signupProperties = (variables: VerifyCodeSignup) => ({
    email: variables.email,
    phone: toPhone(variables.number),
    first_name: variables.profile.first_name,
    last_name: variables.profile.last_name,
    username_type: usernameType,
  });
  const mutation = useFetchMutation<
    VerifyCodeSignup,
    IVerifySmsCodeSignupResult,
    void,
    VerifyCodeSignup,
    FetchError<VerifyCodeSignup, SignupErrorData>
  >({
    fetch: (body) => ({ method: "POST", url: urls.signup(), body }),
    config: {
      onMutate: () => setIsLoggingIn(true),
      onSuccess: (_data, variables) => {
        sendSignupCompleted(signupProperties(variables));
        window.location.replace(next);
      },
      onError: (error, variables) => {
        setIsLoggingIn(false);
        sendSignupFailed({
          ...signupProperties(variables),
          error_category: error.data?.kind ?? null,
          error_message: getSignupErrorDisplay(error),
        });
      },
    },
  });
  return { ...mutation, isLoading: mutation.isPending || isLoggingIn };
};

export const useLoginPhoneNumberValidateQuery = (
  search: LoginPhoneValidationSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ILoginPhoneValidationResults, LoginPhoneValidationSearch>(
    keys.loginPhoneNumberValidation(search),
    {
      fetch: {
        url: urls.verifyLoginPhoneNumber(),
        body: search,
      },
      config: { enabled },
    },
  );
};
